import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  selectExtUserId,
  selectIsLoggedIn,
  selectSSOid,
} from "~/queries/profile/profile.selectors";
import { ProfileData } from "~/types/profile.types";

import { profileOptions } from "./profile.options";

/**
 * Subscribes to the cached logged-in profile. Replaces `ProfileDataContext` as
 * the source of truth — components read the cache directly instead of a value
 * broadcast down a Provider tree. Fetches once, then serves cached data with
 * background refresh, and exposes `isLoading` / `isError` for free.
 *
 * Pass a **module-level** `select` (see `profile.selectors.ts`) to subscribe to
 * a slice: the component then re-renders only when that slice changes. Inline
 * selectors defeat this — they change identity every render.
 *
 * Good: useProfile(selectSSOid)
 * Bad: useProfile((profile: ProfileData) => profile?.SSOid)
 */
const useProfile = <TSelected = ProfileData>(
  select?: (data: ProfileData) => TSelected,
) => useQuery(profileOptions.queries.me(select));

/**
 * The unwrapped-value twin of `useProfile` — returns just `.data` instead of
 * the full query object. Use `useProfile` directly when you need loading/error
 * states.
 *
 * Like `useProfile`, pass a **module-level** `select` (see
 * `profile.selectors.ts`) to subscribe to a slice; omit it for the whole
 * profile. Returns `undefined` while logged out / before the query resolves.
 *
 * Good: useProfileData(selectSSOid)
 * Bad: useProfileData((profile: ProfileData) => profile?.SSOid)
 *
 * { data } will be returned as ProfileData (NormalizedProfile | undefined) - which is the Profile type, but camel case
 *
 */
const useProfileData = <TSelected = ProfileData>(
  select?: (data: ProfileData) => TSelected,
) => useProfile(select).data;

/**
 * Updates any slice of the logged-in profile. `updateProfileData` patches the
 * profile cache on success, so every `useProfile()` subscriber updates without
 * an invalidation here. Pass the field-shaped payload (name, phone, occupation,
 * unsubscribe, …) — see `UpdateProfilePayload` / `IProfile`.
 *
 * const { mutateAsync, isPending, isError } = useUpdateProfile();
 * await mutateAsync({ path: "me", payload: { profession } });
 */
const useUpdateProfile = () => useMutation(profileOptions.mutations.update());

/**
 * Verifies the logged-in user's email. On success the verify mutation flips
 * `emailVerified` in the profile cache, so every `useProfile()` subscriber
 * reflects the verified state without a refetch.
 *
 * const { mutateAsync, isPending } = useVerifyProfileEmail();
 * await mutateAsync({ verificationCode, verificationId });
 */
const useVerifyProfileEmail = () =>
  useMutation(profileOptions.mutations.verifyEmail());

/**
 * Verifies the logged-in user's phone. On success the verify mutation flips
 * `phoneVerified` in the profile cache, so every `useProfile()` subscriber
 * reflects the verified state without a refetch.
 *
 * const { mutateAsync, isPending } = useVerifyProfilePhone();
 * await mutateAsync({ verificationCode, verificationId });
 */
const useVerifyProfilePhone = () =>
  useMutation(profileOptions.mutations.verifyPhone());

/**
 * Sends (or resends) a phone verification code to the user's phone. Resolves
 * with the API response — read `verification_id` from it to submit the code via
 * `useVerifyProfilePhone`.
 *
 * const { mutateAsync, isPending } = useSendPhoneVerificationCode();
 * const data = await mutateAsync();
 */
const useSendPhoneVerificationCode = () =>
  useMutation(profileOptions.mutations.sendPhoneVerificationCode());

/**
 * Pending profile experiences (e.g. updated terms). Shares the same cache entry
 * that `redirectToUpdatedTermsIfPending` warms via `queryClient.query`.
 *
 * const { data, isPending, isError } = useProfileExperiences();
 */
const useProfileExperiences = () =>
  useQuery(profileOptions.queries.experiences());

/**
 * Accepts or declines a pending profile experience. Invalidates the experiences
 * query on success.
 *
 * const { mutateAsync, isPending } = useUpdateProfileExperience();
 * await mutateAsync({ completeExp: true, experienceId });
 */
const useUpdateProfileExperience = () =>
  useMutation(profileOptions.mutations.updateExperience());

/**
 * Sends (or resends) an email verification code to the user's email. The
 * mutation's `onSuccess` stashes the response so `useVerifyProfileEmail` can
 * read the `verification_id` back out — callers only pass the code the user
 * typed.
 *
 * const { mutateAsync, isPending } = useSendEmailVerificationCode();
 * await mutateAsync();
 */
const useSendEmailVerificationCode = () =>
  useMutation(profileOptions.mutations.sendEmailVerificationCode());

/**
 * Updates email subscription
 *
 * const { mutate } = useUpdateEmailSubscription();
 * mutate(subscribed);
 */
const useUpdateEmailSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation(
    profileOptions.mutations.updateEmailSubscription(queryClient),
  );
};

/** Derived state - only add ones that will be use very often
 *  For 1 offs, use `useProfileData(selector)`  instead
 * */
const useIsLoggedIn = (): boolean => useProfile(selectIsLoggedIn).data ?? false;

const useProfileSSOid = (): string | undefined => useProfileData(selectSSOid);

const useIsUserFAEnabled = (): boolean =>
  Boolean(useProfileData(selectExtUserId));

export {
  useProfile,
  useProfileData,
  useIsLoggedIn,
  useIsUserFAEnabled,
  useProfileExperiences,
  useProfileSSOid,
  useSendEmailVerificationCode,
  useSendPhoneVerificationCode,
  useUpdateEmailSubscription,
  useUpdateProfile,
  useUpdateProfileExperience,
  useVerifyProfileEmail,
  useVerifyProfilePhone,
};
